// Estado del TPV en una sola pastilla (puro, testeable). Junta tres
// subsistemas que antes avisaban con bandas a todo lo ancho:
//   - realtime: tiempo real (vista de createRealtimeNotice, staleStatus.js).
//   - worker:   el worker de impresión (Windows / Android) sin conexión.
//   - printer:  una impresora con problema (sin papel, tapa abierta…).
// Solo AVISA: la pastilla nunca tapa el TPV ni desplaza el contenido; los
// cambios de estado se cuentan con un toast corto (createStatusToaster).
import { formatElapsed } from "./staleStatus";

/** El worker cuenta como caído tras estar offline esto SIN interrupción. */
export const WORKER_OFFLINE_AFTER_MS = 30_000;
/** Un mismo aviso no se repite en toast antes de esto (cortes intermitentes). */
export const TOAST_COOLDOWN_MS = 60_000;
/** Duración de los toasts de estado. */
export const TOAST_HIDE_MS = 4_000;

const SEVERITY_RANK = { error: 2, warning: 1 };
// A igual severidad: tiempo real (lo que ve todo el local) > worker > impresora.
const KEY_RANK = { realtime: 3, worker: 2, printer: 1 };

/**
 * Debounce del worker: `update(offline, now)` → true solo cuando lleva
 * ≥ afterMs offline seguido. Se recupera al instante (offline=false).
 */
export function createWorkerOfflineDebounce({ afterMs = WORKER_OFFLINE_AFTER_MS } = {}) {
  let offlineSince = null;
  return {
    update(offline, now = Date.now()) {
      if (!offline) {
        offlineSince = null;
        return false;
      }
      if (offlineSince == null) offlineSince = now;
      return now - offlineSince >= afterMs;
    },
  };
}

/** "Impresora: sin papel" · "2 impresoras con problemas". */
export function printerShortText(issues) {
  if (!issues?.length) return null;
  if (issues.length > 1) return `${issues.length} impresoras con problemas`;
  const reason = String(issues[0]._reason || "con problemas");
  return `Impresora: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`;
}

/**
 * Lista de problemas actuales (vacía = todo bien).
 * @param {object} p
 * @param {{kind: string, variant?: string, elapsedMs?: number}} p.realtime  vista del aviso de tiempo real
 * @param {boolean} p.workerDown        worker caído (ya con el debounce aplicado)
 * @param {Array}   p.offlineWorkers    workers offline (para el detalle)
 * @param {Array}   p.printerIssues     impresoras con problema (con `_reason`)
 * @returns {Array<{key: string, severity: "error"|"warning", short: string}>}
 */
export function buildStatusItems({ realtime, workerDown, offlineWorkers = [], printerIssues = [] } = {}) {
  const items = [];
  if (realtime?.kind === "down") {
    const offline = realtime.variant === "offline";
    items.push({
      key: "realtime",
      severity: "error",
      variant: offline ? "offline" : "realtime",
      elapsedMs: realtime.elapsedMs || 0,
      short: `${offline ? "Sin internet" : "Sin tiempo real"} · ${formatElapsed(realtime.elapsedMs)}`,
    });
  }
  if (workerDown) {
    items.push({
      key: "worker",
      severity: "error",
      short: "Impresión sin conexión",
      detail:
        offlineWorkers.length === 1
          ? `Cocina offline: ${offlineWorkers[0].hostname || offlineWorkers[0].instanceId}`
          : `${offlineWorkers.length} workers de impresión offline`,
    });
  }
  if (printerIssues.length > 0) {
    items.push({
      key: "printer",
      severity: "warning",
      short: printerShortText(printerIssues),
      detail: printerIssues.map((p) => `${p.name} (${p._reason})`).join(" · "),
    });
  }
  return items;
}

/** El problema más grave (el que enseña la pastilla) o null. */
export function pickWorstStatus(items) {
  if (!items?.length) return null;
  return items.reduce((worst, it) => {
    if (!worst) return it;
    const ds = (SEVERITY_RANK[it.severity] || 0) - (SEVERITY_RANK[worst.severity] || 0);
    if (ds !== 0) return ds > 0 ? it : worst;
    return (KEY_RANK[it.key] || 0) > (KEY_RANK[worst.key] || 0) ? it : worst;
  }, null);
}

export const TOAST_TEXT = {
  realtime: "Sin tiempo real · los cambios de otros dispositivos no llegan",
  offline: "Sin conexión a internet · los cambios de otros dispositivos no llegan",
  worker: "Impresión sin conexión · las comandas pueden no estar imprimiéndose",
  printer: (short) => `${short} · revisa la impresora`,
  restored: "Conectado · datos actualizados",
};

/**
 * Toasts de transición. `update({ items, realtimeKind }, now)` llama a
 * `notify(message, variant)`:
 *   - cuando un subsistema PASA a caído (ya tras su umbral), y
 *   - cuando el tiempo real vuelve con la puesta al día (realtimeKind
 *     "restored", que createRealtimeNotice da una sola vez por corte).
 * Un mismo aviso no se repite antes de cooldownMs: sin spam si parpadea.
 */
export function createStatusToaster({ notify, cooldownMs = TOAST_COOLDOWN_MS } = {}) {
  const prevDown = new Set();
  let prevRealtimeKind = null;
  const lastAt = new Map();

  const emit = (key, message, variant, now) => {
    const last = lastAt.get(key);
    if (last != null && now - last < cooldownMs) return;
    lastAt.set(key, now);
    try {
      notify?.(message, variant);
    } catch {
      /* el aviso nunca rompe la operativa */
    }
  };

  return {
    update({ items = [], realtimeKind = null } = {}, now = Date.now()) {
      const down = new Set(items.map((it) => it.key));
      items.forEach((it) => {
        if (prevDown.has(it.key)) return;
        if (it.key === "realtime") {
          emit("realtime", it.variant === "offline" ? TOAST_TEXT.offline : TOAST_TEXT.realtime, "error", now);
        } else if (it.key === "worker") {
          emit("worker", TOAST_TEXT.worker, "error", now);
        } else if (it.key === "printer") {
          emit("printer", TOAST_TEXT.printer(it.short), "warning", now);
        }
      });
      if (realtimeKind === "restored" && prevRealtimeKind !== "restored") {
        emit("realtime.restored", TOAST_TEXT.restored, "success", now);
      }
      prevDown.clear();
      down.forEach((k) => prevDown.add(k));
      prevRealtimeKind = realtimeKind;
    },
  };
}
